import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import { X } from 'lucide-react';
import { t } from '../../lib/i18n';
import { TextField } from '../../components/ui';
import { formatPounds, penceToInput, poundsToPence } from './api';

/** Platform billing sections as links, so each page is addressable and works with the back button. */
export function BillingTabs() {
  const link = ({ isActive }: { isActive: boolean }) =>
    `inline-flex min-h-10 items-center border-b-2 px-3 text-sm font-medium ${isActive ? 'border-brand text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900'}`;
  return (
    <nav aria-label={t('billingUi.tabs')} className="mb-5 flex flex-wrap gap-1 border-b border-slate-200">
      <NavLink end to="/platform/billing" className={link}>
        {t('billingUi.tabs.overview')}
      </NavLink>
      <NavLink to="/platform/billing/plans" className={link}>
        {t('billingUi.tabs.plans')}
      </NavLink>
      <NavLink to="/platform/billing/invoices" className={link}>
        {t('billingUi.tabs.invoices')}
      </NavLink>
      <NavLink to="/platform/billing/export" className={link}>
        {t('billingUi.tabs.export')}
      </NavLink>
    </nav>
  );
}

/** A pounds input holding the text a person typed; onValue gets integer pence, or null while it isn't a valid amount. */
export function MoneyField({ label, value, onValue, hint, allowNegative = false }: { label: string; value: number | null; onValue: (pence: number | null) => void; hint?: string; allowNegative?: boolean }) {
  const [text, setText] = useState(value === null ? '' : penceToInput(value));
  const pence = poundsToPence(text);
  const bad = text.trim() !== '' && (pence === null || (!allowNegative && pence < 0));
  return (
    <TextField
      label={label}
      hint={hint}
      inputMode="decimal"
      value={text}
      error={bad ? t('billingUi.money.invalid') : undefined}
      onChange={(e) => {
        setText(e.target.value);
        const p = poundsToPence(e.target.value);
        onValue(p === null || (!allowNegative && p < 0) ? null : p);
      }}
    />
  );
}

/** Slide-in panel used for invoice details. Escape closes it. */
export function Drawer({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/40">
      <aside role="dialog" aria-modal="true" aria-label={title} className="h-full w-full max-w-2xl overflow-y-auto bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button type="button" aria-label={t('common.close')} onClick={onClose} className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-slate-100">
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
        {children}
      </aside>
    </div>
  );
}

export function Money({ pence }: { pence: number }) {
  return <span className="tabular-nums">{formatPounds(pence)}</span>;
}
